/**
 * Why did the playlist request fail, when hls.js cannot say?
 *
 * The edge answers a geo-block, a referrer rule or a rejected token with a 403
 * that carries no CORS headers, unlike the playlist itself and origin-side
 * answers such as a 404. The browser then hides the response from script:
 * hls.js reports `code: 0`, native HLS a bare media error, and neither can be
 * told apart from a dropped connection by status alone (STREAM-975).
 *
 * What separates them is whether the playlist host answered at all. A
 * `no-cors` request to the same URL settles that: any answer, whatever its
 * status, resolves opaquely, while DNS failure, a refused or dropped
 * connection, and a request an extension blocks all still reject (checked in
 * Chrome, Safari and Firefox). So a request that fails with CORS and
 * succeeds without it was answered, and the only thing that answers without
 * CORS headers is the edge refusing it.
 */

import { discardBody, fetchWithTimeout } from './fetch-with-timeout.ts';
import { PLAYLIST_PROBE_TIMEOUT_MS } from './live-playlist-probe.ts';

export type PlaylistDelivery =
  /** The CDN answered and refuses to serve this viewer the playlist. */
  | 'refused'
  /** The CDN answered that there is no such playlist. */
  | 'missing'
  /** The playlist is being served; whatever failed was not delivery. */
  | 'available'
  /** Nothing answered, timed out, or an answer worth retrying: not classifiable. */
  | 'unknown';

export interface DeliveryProbeOptions {
  fetchImpl?: typeof fetch;
  timeoutMs?: number;
}

/**
 * Deliberately conservative: everything not positively identified comes back
 * `unknown`, so the caller keeps whatever recovery it already had. A timeout
 * is `unknown` too, since a slow edge says nothing about permission.
 */
export async function classifyPlaylistDelivery(
  url: string,
  {
    fetchImpl = globalThis.fetch?.bind(globalThis),
    timeoutMs = PLAYLIST_PROBE_TIMEOUT_MS,
  }: DeliveryProbeOptions = {},
): Promise<PlaylistDelivery> {
  if (!url || typeof fetchImpl !== 'function') return 'unknown';

  const playlist = await fetchWithTimeout(url, { timeoutMs, fetchImpl });
  if ('response' in playlist) {
    const { status } = playlist.response;
    discardBody(playlist.response);
    if (status === 401 || status === 403) return 'refused';
    if (status === 404 || status === 410) return 'missing';
    return playlist.response.ok ? 'available' : 'unknown';
  }
  if (playlist.failure === 'timeout') return 'unknown';

  // The response was hidden from script: an edge refusal without CORS headers,
  // or no answer at all. Only an answer resolves without CORS.
  const opaque = await fetchWithTimeout(url, {
    mode: 'no-cors',
    timeoutMs,
    fetchImpl,
  });
  if (!('response' in opaque)) return 'unknown';
  discardBody(opaque.response);
  return 'refused';
}
